// Radar comparison overlay (04h-5c) — 2–5 players' shapes layered on one wheel, ported from
// renderers-radar.js's renderRadarOverlay via the 04b bridge. The RadarModule dispatcher renders
// this when a module carries a merged-player set (mod._mergedPlayers); a merge/unmerge flips that
// and refreshModuleBody's, swapping between this and RadarSinglePlayer (independent hook orders).
//
// Same imperative-island chart as single-player (radar-chart.ts updateRadarData is already
// multi-entry). Differences from single-player: the player search + season select are hidden (no
// per-player meaning across a merge), a colour-coded legend replaces them (each chip an unmerge +
// drag source), and the table gains one value column per player with a measured header-fit
// (full → surname-only when space is tight, via a ResizeObserver). No filters, no Per90/Total.
//
// The Radar⇄Distribution toggle works here too (04h-5d): Distribution mode hides the wheel and
// renders RadarDistributionOverlay — one marker per player on the shared population curve.
//
// Two data sources, one view (as Stats' comparison): RadarComparison reads the snapshot;
// RadarComparisonLive (?live=1, T2-16b) reads each player's league file. Both draw
// RadarComparisonView.

import { whyNoShape } from "../live/radar.js";
import { ModuleExplainer } from "./ModuleExplainer.js";
import { explainRadarMerged } from "./explain.js";
import { useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { noDataMessage } from "./no-data.js";
import type { ProtoMetricCategory, ProtoPlayerMetric } from "@catbento/shared";
import { type ProtoModuleProps, getModState, setModState } from "./bridge.js";
import { SubControlsSlot } from "./SubControlsSlot.js";
import { StyledSelect, type StyledSelectItem } from "./StyledSelect.js";
import { RadarAxisEditor, RadarEditAxesButton } from "./RadarAxisEditor.js";
import { RadarDistributionOverlay } from "./RadarDistributionOverlay.js";
import { humanizeSeasonCode } from "./league-table-view.js";
import {
  axisValuesMap,
  metricFraction,
  isLowerBetter,
  withMinutesRow,
  buildMetricCatalog,
  polygonPoints,
  humanizePositionTemplate,
  playerSpread,
  TEMPLATE_ORDER,
  type RadarAxis,
  type RadarAxisWithRange,
  type RadarRange,
} from "./radar-view.js";
import {
  CHART_SKELETON_HTML,
  ensureChartFrame,
  updateRadarData,
  teardownRadarChart,
  setAxisTicksActive,
  type RadarEntry,
} from "./radar-chart.js";
import { formatMetricValue } from "../engines/stats-filter-kit.js";
import { useRadarAxes, useRadarCategories } from "../hooks/useRadarAxes.js";
import { useRadarRanges } from "../hooks/useRadarRanges.js";
import { useMetricCategories } from "../hooks/useMetricCategories.js";
import { useLiveRadarComparison, useLiveRadarGroups } from "../hooks/useRadarView.js";
import { readSnapshot, snapshotPlayersOrNone, type RawPlayerRecord } from "../data/snapshot.js";
import { getPlayerShortName, getPlayerInitialName, getPlayerLastName } from "../data/player-utils.js";
import { setStickyPlayer } from "../canvas/sticky-player.js";
import { playerById, playerIdOf, type PlayerId } from "../data/players.js";
import { pickedPlayer } from "../live/picked-players.js";
import { startPlayerTokenDrag } from "../canvas/menu-drag.js";
import { unmergePlayer } from "../canvas/drop-actions.js";
import { hideTooltip, showTooltipAt } from "../canvas/tooltip.js";

// Per-player colour/letter identity (CSS: .marker-a../.compare-chip-a..), fixed at merge time.
const COMPARE_LETTERS = ["a", "b", "c", "d", "e"];

// Full names default on; drop every player column to surname-only together once the table would
// need to scroll to show them (renderers-radar.js:658 fitRadarTableHeaderNames).
function fitRadarTableHeaderNames(wrap: HTMLElement): void {
  const nameEls = wrap.querySelectorAll<HTMLElement>(".radar-th-name");
  if (!nameEls.length) return;
  nameEls.forEach((el) => (el.textContent = el.dataset.full ?? ""));
  if (wrap.scrollWidth > wrap.clientWidth + 1) nameEls.forEach((el) => (el.textContent = el.dataset.short ?? ""));
}

/** The axes' chart + table halves, each with its scale (the same steps on both sites). */
function useAxesWithRanges(axes: readonly RadarAxis[], tableAxes: readonly RadarAxis[], ranges: Map<string, RadarRange>) {
  const chartAxes = useMemo<RadarAxisWithRange[]>(
    () => axes.map((a) => ({ ...a, ...(ranges.get(a.key) ?? { min: 0, max: 0 }) })),
    [axes, ranges],
  );
  const tableAxesWithRange = useMemo<RadarAxisWithRange[]>(
    () => tableAxes.map((a) => ({ ...a, ...(ranges.get(a.key) ?? { min: 0, max: 0 }) })),
    [tableAxes, ranges],
  );
  return { chartAxes, tableAxesWithRange };
}

/** The snapshot site: each player's season record, scaled on the snapshot population. */
export function RadarComparison({ mod, body }: ProtoModuleProps) {
  const [, forceRender] = useReducer((x: number) => x + 1, 0);
  const players = readSnapshot().players;
  // The snapshot population — scanned only by the Distribution overlay (rendered only in that view).
  const population = useMemo(() => Object.values(readSnapshot().players) as RawPlayerRecord[], []);
  const merged = getModState<PlayerId[]>(mod, "_mergedPlayers") ?? [];
  const mergedSeasons = getModState<string[]>(mod, "_mergedSeasons");
  const mergedColors = getModState<string[]>(mod, "_mergedColors");
  const customAxes = getModState<string[]>(mod, "_radarCustomAxes") ?? null;

  const categories = useMetricCategories();
  const catalog = useMemo(() => buildMetricCatalog(categories), [categories]);

  const records = merged.map((id) => playerById(players, id)).filter(Boolean) as RawPlayerRecord[];
  // Axes default to the TARGET player's template (the radar that was on screen before the merge),
  // switchable via the template select — one shared axis set for the whole group (rrjs:1601).
  const targetTemplate = getModState<string>(mod, "_selectedRadarTemplate") || records[0]?.positionTemplate || "";

  const axes = useRadarAxes(targetTemplate, customAxes);
  const tableAxes = useMemo(() => withMinutesRow(axes, catalog), [axes, catalog]);
  const ranges = useRadarRanges(targetTemplate, tableAxes);
  const { chartAxes, tableAxesWithRange } = useAxesWithRanges(axes, tableAxes, ranges);

  const seasonCodes = records.map((p, i) => {
    const chosen = mergedSeasons?.[i];
    return chosen && p.seasons?.[chosen] ? chosen : p.currentSeason ?? "";
  });
  const letters = (mergedColors && mergedColors.length ? mergedColors : COMPARE_LETTERS).slice(0, records.length);

  // Every player's fraction is scaled against the TARGET template's range for that axis, even a
  // merged-in player from a different template — one shared set of spokes only means something if a
  // given distance-from-centre means the same for every polygon (rrjs:1710). Values (per table
  // axis) drive the table; the chart uses the chart-axis subset.
  const valuesList = useMemo(
    () => records.map((p, i) => axisValuesMap(p, seasonCodes[i]!, tableAxesWithRange)),
    [records.map((p) => p.name).join("|"), seasonCodes.join("|"), tableAxesWithRange],
  );

  if (!records.length || records.length !== merged.length) {
    return <div className="stats-empty">Comparison unavailable.</div>;
  }

  return (
    <RadarComparisonView
      mod={mod}
      body={body}
      refresh={forceRender}
      template={targetTemplate}
      categories={categories}
      catalog={catalog}
      chartAxes={chartAxes}
      tableAxes={tableAxesWithRange}
      members={records.map((p, i) => ({ id: playerIdOf(p), named: p, seasonCode: seasonCodes[i]! }))}
      valuesList={valuesList}
      letters={letters}
      population={population}
      empty={chartAxes.length > 0 ? null : noDataMessage("radar", getPlayerShortName(records[0]), null)}
    />
  );
}

/**
 * `?live=1` (T2-16b): the same comparison on real numbers. Each player's shape = her league, her
 * current season, her minutes at the template (Q-009) — the one-player Radar's steps, all players
 * at once. ONE scale for the tile: the first (the tile's own) player's league file at the template,
 * as the prototype scales every shape on the tile's own population. The drop rules keep men and
 * women apart.
 */
export function RadarComparisonLive({ mod, body }: ProtoModuleProps) {
  const [, forceRender] = useReducer((x: number) => x + 1, 0);
  const merged = getModState<PlayerId[]>(mod, "_mergedPlayers") ?? [];
  // Each player's season at the merge (null = current): the same player twice = year-on-year.
  const mergedSeasons = getModState<(string | null)[]>(mod, "_mergedSeasons");
  const mergedColors = getModState<string[]>(mod, "_mergedColors");
  const customAxes = getModState<string[]>(mod, "_radarCustomAxes") ?? null;

  const categories = useRadarCategories();
  const catalog = useMemo(() => buildMetricCatalog(categories), [categories]);

  const groups = useLiveRadarGroups(merged, mergedSeasons ?? []);
  // The template the tile showed before the merge, else the first player's position in her file.
  const template = getModState<string>(mod, "_selectedRadarTemplate") || groups.defaultTemplate || "";
  const axes = useRadarAxes(template, customAxes);
  const tableAxes = useMemo(() => withMinutesRow(axes, catalog), [axes, catalog]);
  const { valuesList, ranges, population } = useLiveRadarComparison(groups, merged, template, tableAxes);
  const { chartAxes, tableAxesWithRange } = useAxesWithRanges(axes, tableAxes, ranges);

  const letters = (mergedColors && mergedColors.length ? mergedColors : COMPARE_LETTERS).slice(0, merged.length);
  const members = merged.map((id, i) => ({ id, named: namedPlayer(id), seasonCode: String(groups.groups[i]!.seasonId) }));
  // No scale without the first player's file (e.g. her league's season isn't built yet).
  const first = groups.groups[0]!;
  const empty =
    first.kind === "none"
      ? `No radar: ${first.label}`
      : chartAxes.length > 0
        ? null
        : noDataMessage("radar", getPlayerShortName(members[0]!.named), null);

  const explanation = explainRadarMerged({
    players: members.map((m) => ({ name: getPlayerShortName(m.named), season: humanizeSeasonCode(m.seasonCode) })),
    template: humanizePositionTemplate(template),
    scaleFrom: first.kind === "file" ? `${first.league.label}, ${humanizeSeasonCode(String(first.seasonId))}` : null,
  });
  return (
    <>
      <ModuleExplainer mod={mod} paragraphs={explanation} />
      <RadarComparisonView
        mod={mod}
        body={body}
        refresh={forceRender}
        template={template}
        categories={categories}
        catalog={catalog}
        chartAxes={chartAxes}
        tableAxes={tableAxesWithRange}
        members={members}
        valuesList={valuesList}
        letters={letters}
        population={population}
        empty={empty}
        notes={members.flatMap((m, i) => {
          const g = groups.groups[i]!;
          if (Object.keys(valuesList[i] ?? {}).length > 0) return [];
          const name = getPlayerShortName(m.named);
          if (g.kind !== "file") return [`No shape for ${name}: ${g.label}.`];
          return [whyNoShape(g.group.file, m.id, template, name, `${g.league.label} ${humanizeSeasonCode(m.seasonCode)}`)];
        })}
      />
    </>
  );
}

/** Who a live comparison's player is: the snapshot's record, else her live pick (her name). */
function namedPlayer(id: PlayerId): { name?: string } {
  const p = playerById(snapshotPlayersOrNone(), id) ?? pickedPlayer(id);
  if (!p) throw new Error(`Player id ${id} is neither in the snapshot nor a live pick`);
  return p;
}

/** One player on the comparison, as the view draws them. */
interface RadarMember {
  id: PlayerId;
  /** Her name (the legend, the column header, the wedge tooltip). */
  named: { name?: string };
  seasonCode: string;
}

interface ViewProps {
  mod: HTMLElement;
  body: HTMLElement;
  /** Re-render the data side (a template / view / axis change reads new state off `mod`). */
  refresh: () => void;
  template: string;
  categories: ProtoMetricCategory[];
  catalog: Record<string, ProtoPlayerMetric>;
  chartAxes: RadarAxisWithRange[];
  tableAxes: RadarAxisWithRange[];
  members: RadarMember[];
  /** Per player, her value per table axis — memoised by the caller (a new list restarts the
   *  chart's transition). */
  valuesList: readonly Record<string, number | null | undefined>[];
  letters: string[];
  population: readonly RawPlayerRecord[];
  /** Instead of the chart + table (the legend stays), or null. */
  empty: string | null;
  /** Live: why a player has no shape (one line each), under the legend. */
  notes?: readonly string[];
}

/** The legend, wheel and table — the same on both sites. */
function RadarComparisonView({
  mod,
  body,
  refresh,
  template,
  categories,
  catalog,
  chartAxes,
  tableAxes: tableAxesWithRange,
  members,
  valuesList,
  letters,
  population,
  empty,
  notes = [],
}: ViewProps) {
  const [editorOpen, setEditorOpen] = useState(false);
  const editBtnRef = useRef<HTMLButtonElement>(null);
  const svgWrapRef = useRef<HTMLDivElement>(null);
  const tableWrapRef = useRef<HTMLDivElement>(null);

  const prefixes = members.map((m) => `${getPlayerLastName(m.named)} ${humanizeSeasonCode(m.seasonCode)}`);
  // fractions[i] is null for an axis a player has no value for (never a fabricated point).
  const chartEntries = useMemo<RadarEntry[]>(
    () =>
      valuesList.map((vm, i) => {
        const fractions = chartAxes.map((a) => metricFraction(vm[a.key], { min: a.min, max: a.max }, isLowerBetter(a.key)));
        const values = chartAxes.map((a) => (vm[a.key] === undefined ? null : vm[a.key]));
        return { colorLetter: letters[i]!, fractions, values, tooltipPrefix: prefixes[i]! };
      }),
    [valuesList, chartAxes, letters.join(""), prefixes.join("|")],
  );

  const hasChart = empty === null;
  // Distribution works for a merged group too (04h-5d) — one marker per player on the shared
  // population curve. mod._radarView is the same field single-player uses.
  const isDist = getModState<string>(mod, "_radarView") === "distribution";

  // Comparison keeps main.js's strip visible (for the template select) but hides its player search —
  // there's no single "current player" to search across a merge (rrjs:1546). Restore on unmount.
  useEffect(() => {
    body.style.justifyContent = "flex-start";
    const strip = mod.querySelector<HTMLElement>(":scope > .module-sub-controls");
    if (!strip) return;
    const search = strip.querySelector<HTMLElement>(".player-search-wrap");
    if (!search) return;
    const prev = search.style.display;
    search.style.display = "none";
    return () => {
      search.style.display = prev;
    };
  }, [mod, body]);

  // Build the chart skeleton for this comparison's life (imperative island); tear down on unmount.
  // A different host than single-player, so a merge/unmerge naturally swaps the two. Keyed on
  // `hasChart`: the host only exists with a chart (live: a template picked after "no data").
  useEffect(() => {
    const wrap = svgWrapRef.current;
    if (!wrap) return;
    wrap.innerHTML = CHART_SKELETON_HTML;
    const svg = wrap.querySelector<SVGSVGElement>(".radar-svg");
    return () => {
      if (svg) teardownRadarChart(svg);
      wrap.innerHTML = "";
    };
  }, [hasChart]);

  // Draw the overlay: one polygon per player (the d3 join handles 2–5), no teardown in cleanup.
  useEffect(() => {
    const svg = svgWrapRef.current?.querySelector<SVGSVGElement>(".radar-svg");
    if (!svg || !hasChart) return;
    ensureChartFrame(svg, chartAxes);
    updateRadarData(svg, chartAxes, chartEntries);
  }, [chartAxes, chartEntries, hasChart]);

  // Table header-fit: measure with full names, drop to surname-only if the table would scroll.
  // Re-run on data change AND on resize (a ResizeObserver, disconnected in cleanup). rrjs:1068.
  useLayoutEffect(() => {
    const wrap = tableWrapRef.current;
    if (!wrap || !hasChart) return;
    fitRadarTableHeaderNames(wrap);
    const ro = new ResizeObserver(() => fitRadarTableHeaderNames(wrap));
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [valuesList, tableAxesWithRange, letters, hasChart]);

  const onTemplateChange = (next: string) => {
    setModState(mod, "_selectedRadarTemplate", next);
    // A customization is scoped to the axis set it was built against (rrjs:1605).
    setModState(mod, "_radarCustomAxes", null);
    refresh();
  };
  const onToggleView = () => {
    setModState(mod, "_radarView", isDist ? "radar" : "distribution");
    refresh();
  };

  // A legend chip is a drag source (→ Bio on empty canvas, or merges into another Stats/Radar),
  // carrying its OWN season — via startPlayerTokenDrag (drag-to-add).
  const onChipDrag = (e: ReactPointerEvent<HTMLElement>, id: PlayerId, season: string) => {
    if (window.innerWidth <= 768) return;
    e.preventDefault();
    e.stopPropagation();
    setStickyPlayer(id);
    startPlayerTokenDrag(e.nativeEvent, { _selectedSeason: season });
  };

  // Legend → chart hover: a hovered chip's player shows as an individual radar (their banded fill,
  // via the shared clip re-pointed at their shape) while the others dim (rrjs:592 wireRadarLegendHover).
  const withSvg = (fn: (svg: SVGSVGElement) => void) => {
    const svg = svgWrapRef.current?.querySelector<SVGSVGElement>(".radar-svg");
    if (svg) fn(svg);
  };
  const onLegendEnter = (i: number) => {
    withSvg((svg) => {
      const polyG = svg.querySelector<SVGGElement>(".radar-polygons");
      const bandedFillG = svg.querySelector<SVGGElement>(".radar-banded-fill");
      const clipPolygon = svg.querySelector<SVGPolygonElement>(".radar-clip-polygon");
      if (!polyG || !bandedFillG || !clipPolygon) return;
      const target = polyG.querySelector(`.radar-polygon.marker-${letters[i]}`);
      Array.from(polyG.children).forEach((poly) => {
        poly.classList.toggle("compare-highlighted", poly === target);
        poly.classList.toggle("compare-dimmed", poly !== target);
      });
      clipPolygon.setAttribute("points", polygonPoints(chartAxes, chartEntries[i]!.fractions));
      polyG.parentNode?.insertBefore(bandedFillG, polyG.nextSibling); // raise the banded fill above the dimmed shapes
    });
  };
  const onLegendLeave = () => {
    withSvg((svg) => {
      const polyG = svg.querySelector<SVGGElement>(".radar-polygons");
      const bandedFillG = svg.querySelector<SVGGElement>(".radar-banded-fill");
      const clipPolygon = svg.querySelector<SVGPolygonElement>(".radar-clip-polygon");
      if (!polyG || !bandedFillG || !clipPolygon) return;
      Array.from(polyG.children).forEach((poly) => poly.classList.remove("compare-highlighted", "compare-dimmed"));
      clipPolygon.setAttribute("points", "");
      polyG.parentNode?.insertBefore(bandedFillG, polyG); // back to its fixed spot before the polygons
    });
  };

  // Table row → chart wedge hover (same as single-player): highlight the wedge + brighten its ticks
  // + raise the wedge tooltip. The Minutes row has no wedge → a no-op.
  const wedgeFor = (svg: SVGSVGElement, axisKey: string): SVGElement | null =>
    [...svg.querySelectorAll<SVGElement>(".radar-wedge")].find((w) => w.dataset.axisKey === axisKey) ?? null;
  const onRowEnter = (axisKey: string) =>
    withSvg((svg) => {
      const wedge = wedgeFor(svg, axisKey);
      if (!wedge) return;
      wedge.classList.add("row-hover");
      setAxisTicksActive(svg, axisKey, true);
      if (wedge.dataset.tooltip) {
        const rect = wedge.getBoundingClientRect();
        showTooltipAt(wedge.dataset.tooltip, rect.left + rect.width / 2, rect.top + rect.height / 2);
      }
    });
  const onRowLeave = (axisKey: string) => {
    withSvg((svg) => {
      const wedge = wedgeFor(svg, axisKey);
      if (wedge) wedge.classList.remove("row-hover");
      setAxisTicksActive(svg, axisKey, false);
    });
    hideTooltip();
  };

  const templateItems: StyledSelectItem[] = TEMPLATE_ORDER.map((t) => ({ value: t, label: humanizePositionTemplate(t) }));

  return (
    <>
      {/* Template select, the Radar⇄Distribution toggle (works for a merged group too — 04h-5d),
          then the edit button. No season/search (hidden across a merge). */}
      <SubControlsSlot mod={mod}>
        <StyledSelect items={templateItems} value={template} onChange={onTemplateChange} wrapClass="template-select-wrap" extraClass="template-select" />
        <button type="button" className="module-switch-btn radar-view-toggle" onClick={onToggleView}>
          {isDist ? "Radar" : "Distribution"}
        </button>
        <RadarEditAxesButton open={editorOpen} btnRef={editBtnRef} onToggle={() => setEditorOpen((o) => !o)} />
      </SubControlsSlot>

      {editorOpen && editBtnRef.current && (
        <RadarAxisEditor
          anchor={editBtnRef.current}
          mod={mod}
          template={template}
          categories={categories}
          catalog={catalog}
          onChange={refresh}
          onClose={() => setEditorOpen(false)}
        />
      )}

      <div className="compare-wrap">
        <div className="compare-legend">
          {members.map((p, i) => (
            <span
              key={i}
              className={`compare-chip compare-chip-${letters[i]}`}
              style={window.innerWidth > 768 ? { cursor: "grab" } : undefined}
              onPointerDown={(e) => {
                if ((e.target as HTMLElement).closest(".compare-remove")) return;
                onChipDrag(e, p.id, p.seasonCode);
              }}
              onMouseEnter={() => onLegendEnter(i)}
              onMouseLeave={onLegendLeave}
            >
              <span className="compare-dot" />
              {getPlayerInitialName(p.named)} <span className="compare-chip-season">({humanizeSeasonCode(p.seasonCode)})</span>
              <span className="compare-remove" onClick={() => unmergePlayer(mod, i)}>
                ✕
              </span>
            </span>
          ))}
        </div>

        {notes.map((n, k) => (
          <div className="radar-shape-note" key={k}>
            {n}
          </div>
        ))}
        {!hasChart ? (
          <div className="stats-empty">{empty}</div>
        ) : (
          <div className="radar-body-layout">
            {/* Same mutual exclusion as single-player: Distribution hides the wheel SVG (kept
                mounted) and takes over the table slot with one marker per player on the curve. */}
            <div className="radar-svg-wrap" ref={svgWrapRef} style={isDist ? { display: "none" } : undefined} />
            <div className="radar-table-wrap" ref={tableWrapRef}>
              {isDist ? (
                <RadarDistributionOverlay
                  spread={playerSpread(population, template, humanizePositionTemplate(template))}
                  axes={chartAxes}
                  names={members.map((m) => getPlayerLastName(m.named))}
                  valuesList={valuesList}
                  letters={letters}
                />
              ) : (
              <table className="radar-table">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Min</th>
                    {members.map((p, i) => (
                      <th key={i}>
                        <span
                          className={`marker-${letters[i]} radar-th-name`}
                          data-full={getPlayerShortName(p.named)}
                          data-short={getPlayerLastName(p.named)}
                        >
                          {getPlayerShortName(p.named)}
                        </span>
                      </th>
                    ))}
                    <th>Max</th>
                  </tr>
                </thead>
                <tbody>
                  {tableAxesWithRange.map((axis) => (
                    <tr key={axis.key} data-axis-key={axis.key} onMouseEnter={() => onRowEnter(axis.key)} onMouseLeave={() => onRowLeave(axis.key)}>
                      <td className="radar-table-metric">{axis.label}</td>
                      <td>{formatMetricValue(axis.min, axis.outputType)}</td>
                      {members.map((_, i) => (
                        <td key={i} className={`radar-table-value marker-${letters[i]}`}>
                          {formatMetricValue(valuesList[i]![axis.key], axis.outputType)}
                        </td>
                      ))}
                      <td>{formatMetricValue(axis.max, axis.outputType)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
